import React, { useState, useEffect, useCallback } from "react";
import { Redirect } from "react-router-dom";
import SubPageTemplate from "./templates/SubPageTemplate";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import InfiniteScroll from "react-infinite-scroller";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dropdown from "../components/inputs/Dropdown";
import inventoryMissingImg from "../images/inventory_missing.png";
import characterMap from "../json/characters";
import {
  profileAPI,
  headerAPI,
  _link,
  CDN_URI_BASE,
  CDN_URI,
  numberWithCommas,
  handleMissingImg,
  prefixURI
} from "../common/utilities";
import styles from "./InventoryPage.module.scss";

const weaponImgPath = `${CDN_URI_BASE}weapon/300_169/`;
const charImgPath = `${CDN_URI_BASE}`;
const CancelToken = axios.CancelToken;
const source = CancelToken.source();
let cancel = null;
const dataFetchLength = 20;

function FilterBar(props) {
  const { handleFilterClick, handleSortingClick, categoryID, orderID } = props;

  const { t, i18n } = useTranslation();

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));

  const categories = [
    { value: 0, label: t("All") },
    { value: 1, label: t("Weapons") },
    { value: 2, label: t("Characters") },
    { value: 3, label: t("Namecards") },
    { value: 4, label: t("Sprays") },
    { value: 5, label: t("Costumes") },
    { value: 6, label: t("VIP") }
  ];
  const sortingOrders = [
    { value: 1, label: t("Received Date") },
    { value: 2, label: t("A-Z") },
    { value: 3, label: t("Z-A") }
  ];

  const handleFilterDropdownChange = val => {
    handleFilterClick(val)();
  };

  const handleOrderDropdownChange = val => {
    handleSortingClick(val)();
  };

  const desktopContent = (
    <div className={styles.filter_bar_container}>
      <div className={styles.filter_types}>
        {categories.map(item => (
          <span
            className={clsx(categoryID == item.value && styles.selected)}
            onClick={handleFilterClick(item.value)}
          >
            {item.label}
          </span>
        ))}
      </div>
      <div className={styles.filter_sorting}>
        <span className={styles.filter_sort_label}>{`${t('Sort')}: `}</span>
        {sortingOrders.map(item => (
          <span
            onClick={handleSortingClick(item.value)}
            className={clsx(orderID == item.value && styles.selected)}
          >
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );

  const mobileContent = (
    <div className={styles.page_content_mobile}>
      <Dropdown
        options={categories}
        onChange={handleFilterDropdownChange}
        selectedValue={categoryID}
      />
      <Dropdown
        options={sortingOrders}
        onChange={handleOrderDropdownChange}
        selectedValue={orderID}
      />
    </div>
  );

  return mobile ? mobileContent : desktopContent;
}

function InventoryItem({ data }) {
  let itemImg = inventoryMissingImg;
  let vipIconClass = '';

  vipIconClass = (data.VIP_FLAG) ? ' icon-vip' : '';

  try {
    switch (data.ITEM_CATEGORY) {
      case 1: //weapon
        itemImg = `${CDN_URI_BASE}web/inventory/weapons/300_169/${data.ITEM_CODE}.png`;
        break;
      case 2: //character
        itemImg = `${CDN_URI_BASE}web/inventory/characters/300_169/${data.ITEM_CODE}.png`;
        break;
      case 3: //name card
        itemImg = `${CDN_URI_BASE}web/inventory/namecards/300_169/${data.ITEM_CODE}.png`;
        break;
      case 4: //spray
        itemImg = `${CDN_URI_BASE}web/inventory/sprays/300_169/${data.ITEM_CODE}.png`;
        break;
      case 5: //costume
        itemImg = `${CDN_URI_BASE}web/inventory/costumes/300_169/${data.ITEM_CODE}.png`;
        break;
    }
  } catch {
    //do nothing
  }

  return (
    <Grid item xs={6} sm={4} md={3} classes={{ root: styles.item }}>
      <div className={styles.item_img_container + vipIconClass}>
        <img
          className={styles.item_img}
          src={itemImg}
          onError={handleMissingImg(inventoryMissingImg)}
        />
      </div>
      <span className={styles.item_name}>{data.DISPLAY_NAME}</span>
    </Grid>
  );
}

function PageContent(props) {
  const {
    inventoryItems,
    statusMsg,
    handleLoadInfiniteScroll,
    handleHasMoreInfiniteScroll,
    loading
  } = props;

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));

  const content = loading ? null : inventoryItems.length > 0 ? (
    inventoryItems.map(item => <InventoryItem data={item} />)
  ) : (
    <p className={styles.no_info_msg}>{statusMsg}</p>
  );

  return (
    <div style={{ overflow: "hidden" }}>
      <InfiniteScroll
        pageStart={0}
        loadMore={handleLoadInfiniteScroll}
        hasMore={handleHasMoreInfiniteScroll()}
        loader={
          <div className="loader" key={0}>
            {/* Loading ... */}
          </div>
        }
        useWindow={true}
        threshold={100}
      >
        <Grid
          container
          spacing={mobile ? 2 : 5}
          // justify="space-between"
          classes={{
            root: clsx(styles.items_container, mobile && styles.mobile)
          }}
        >
          {content}
        </Grid>
      </InfiniteScroll>
    </div>
  );
}

export default function InventoryPage(props) {
  const { match, devUSN } = props;

  const [didInitialDataLoad, setDidInitialDataLoad] = useState(false);
  const [categoryID, setCategoryID] = useState(0);
  const [orderID, setOrderID] = useState(1);
  const [inventoryTotalCount, setInventoryTotalCount] = useState(0);
  const [categoryTotalCount, setCategoryTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [inventoryData, setInventoryData] = useState();
  const [inventoryView, setInventoryView] = useState([]);
  const [headerData, setHeaderData] = useState();
  const [statusMsg, setStatusMsg] = useState("");
  const [pageStatus, setPageStatus] = useState(true);

  const { t, i18n } = useTranslation();

  // const CancelToken = axios.CancelToken;
  // const [source, setSource] = useState(CancelToken.source());
  const cancelExecutor = c => {
    source = c;
  };

  const usn = devUSN ? devUSN : match ? match.params.usn : null;

  //can make this a closure that contains cancelToken as private
  //variable, but I will leave it for now and be careful to
  //modify cancelToken external from other places (if any)
  const fetchData = (categoryID, orderID, start, end, isAppendData) => {
    if (!isAppendData) {
      setLoading(true);
    }

    if (cancel && !isAppendData) {
      cancel();
    }

    axios
      .get(
        `${profileAPI}?usn=${usn}&command=inventory&category=${categoryID}&order=${orderID}&start=${start}&end=${end}`,
        {
          cancelToken: new CancelToken(c => {
            cancel = c;
          })
        }
      )
      .then(
        res => {
          if (res.data.APIresult === "000") {
            if (isAppendData) {
              setInventoryView(
                inventoryView.concat(res.data.dsInventoryResult)
              );
            } else {
              setInventoryView(res.data.dsInventoryResult);
            }
            setCategoryTotalCount(res.data.dsInventoryTotal[0].CNT);
            //consider setting status message and checking if
            //return api status to determine if api successful
            if (!isAppendData) {
              setStatusMsg(
                res.data.dsInventoryResult.length > 0
                  ? ""
                  : t('There is no information to display.')
              );
            }
          } else {
            setStatusMsg(t("An error occured."));
          }
        },
        e => {
          // console.log("infinite rejected");
        }
      )
      .finally(() => {
        if (!isAppendData) {
          setLoading(false);
        }
      });

    setDidInitialDataLoad(true);
  };

  // Similar to componentDidMount and componentDidUpdate:
  //
  // https://stackoverflow.com/questions/53120972/how-to-call-loading-function-with-react-useeffect-only-once
  useEffect(() => {
    //api call to set data will be used
    if (usn) {
      axios.get(`${headerAPI}&usn=${usn}`).then(res => {
        if (res.data.p_o_ErrID === 1)
          setHeaderData(res.data.dsProfileHeaderInfo[0]);
        else setPageStatus(false);
      });
      fetchData(categoryID, orderID, 1, dataFetchLength);
    }
  }, []);

  if (!didInitialDataLoad) {
    return null;
  }

  const handleFilterClick = newCategoryID => {
    return () => {
      if (newCategoryID != categoryID) {
        setCategoryID(newCategoryID);
        fetchData(newCategoryID, orderID, 1, dataFetchLength);
      }
    };
  };

  const handleSortingClick = newOrderID => {
    return () => {
      if (newOrderID != orderID) {
        setOrderID(newOrderID);
        fetchData(categoryID, newOrderID, 1, dataFetchLength);
      }
    };
  };

  const handleLoadInfiniteScroll = () => {
    fetchData(
      categoryID,
      orderID,
      inventoryView.length + 1,
      inventoryView.length + dataFetchLength,
      true
    );
  };

  const handleHasMoreInfiniteScroll = () => {
    return !loading && inventoryView.length < categoryTotalCount;
  };

  //created MyFitlerBar because we will call this
  //component with these same props multiple times
  const MyFilterBar = () => (
    <FilterBar
      categoryID={categoryID}
      handleFilterClick={handleFilterClick}
      handleSortingClick={handleSortingClick}
      categoryID={categoryID}
      orderID={orderID}
    />
  );

  return (
    <SubPageTemplate
      title={t(
        'Inventory of {{ign}} - Crossfire Player Profile',
        headerData ? headerData.NICK : ""
      )}
      headerData={headerData}
      headingLeft={t('Inventory')}
      headingRight={t(
        '{{x}} Items',
        { x: numberWithCommas(categoryTotalCount) })}
    >
      {!pageStatus ? <Redirect to={`${prefixURI}/error`} /> : null}
      <div className={styles.page_content}>
        <MyFilterBar />
        <PageContent
          inventoryItems={inventoryView}
          handleLoadInfiniteScroll={handleLoadInfiniteScroll}
          handleHasMoreInfiniteScroll={handleHasMoreInfiniteScroll}
          statusMsg={statusMsg}
          loading={loading}
        />
        <MyFilterBar />
      </div>
    </SubPageTemplate>
  );
}
